/**
 * GmEventService — client-side service + hooks for the GM event system.
 *
 * Provides:
 *   - `useGmEvent()` hook: subscribes to gmEvent* broadcasts, exposes
 *     current event state + scoreboard + announcements.
 *   - Imperative send helpers: createEvent, startEvent, joinEvent, etc.
 *
 * Pattern follows useParty.ts / useTrade.ts — useColyseusMessages for
 * inbound, ServiceRegistry → room.send for outbound.
 *
 * @see docs/design/GM_EVENT_INTEGRATION_SPEC.md
 */

import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import { useColyseusMessages } from '../ui/hooks/useColyseusMessages';
import type {
  GmEventAnnouncementWire,
  GmEventCreatedWire,
  GmEventPhaseChangedWire,
  GmEventScoreboardWire,
  GmEventEndedWire,
  GmEventCancelledWire,
  GmEventFishingCatchWire,
  GmEventFishingPlayerStateWire,
  GmEventRacePlayerStateWire,
  GmEventRaceFinishWire,
  GmEventTemplateInfo,
  GmEventTemplateListWire,
} from '../../shared/types/gm/GmEventWire';
import type { GmEventScoreboard } from '../../shared/types/gm/GmEvent';

export interface GmEventAnnouncement {
  eventId: string;
  title: string;
  phase: string;
  secondsRemaining: number;
  participantCount: number;
  maxParticipants: number;
  bornAt: number;
}

export interface GmEventState {
  announcement: GmEventAnnouncement | null;
  scoreboard: GmEventScoreboard | null;
  ended: { eventId: string; winnerIds: string[]; scoreboard: GmEventScoreboard } | null;
  cancelled: { eventId: string; reason?: string } | null;
  fishingCatch: { sessionId: string; fishName: string; rarity: string; weight: number; points: number } | null;
  fishingPlayerState: { sessionId: string; status: string; totalScore: number; totalWeight: number } | null;
  racePlayerState: {
    sessionId: string;
    status: string;
    checkpointIndex: number;
    lap: number;
    place?: number;
    finishTimeMs?: number;
    totalCheckpoints: number;
  } | null;
  raceFinish: { sessionId: string; place: number; finishTimeMs: number } | null;
}

const ANNOUNCEMENT_TIMEOUT_MS = 10_000;

/**
 * Module-level mirror of the latest GmEventState, updated by the useGmEvent
 * hook on every state change. This allows non-React code (e.g. the input
 * system's fishing cast/reel handler) to read the current event state
 * without a React context.
 */
let currentState: GmEventState = {
  announcement: null,
  scoreboard: null,
  ended: null,
  cancelled: null,
  fishingCatch: null,
  fishingPlayerState: null,
  racePlayerState: null,
  raceFinish: null,
};

/** Read the latest GM event state (for non-React code). */
export function getGmEventState(): GmEventState {
  return currentState;
}

function getRoom(): any {
  return (globalThis as any).nm?.getRoom?.() ?? null;
}

function sendToServer(type: string, data?: any): void {
  const room = getRoom();
  if (room) room.send(type, data ?? {});
}

// ── lastEnded — SHARED persistent results store (Event Control §3.1) ──
//
// Unlike the per-hook `ended` (15s flash, one copy per mounted useGmEvent),
// the last race's results are ONE piece of shared state: RaceResultsHud
// shows it, the Event Control post-race section reads it for run-it-back,
// and a dismiss in either place clears both. Populated by every
// gmEventEnded (live or the server's join-time re-send); cleared when a
// NEW event reaches staging, or explicitly via clearLastEnded().

type GmEventEndedState = { eventId: string; winnerIds: string[]; scoreboard: GmEventScoreboard } | null;

let lastEndedState: GmEventEndedState = null;
const lastEndedListeners = new Set<() => void>();

function setLastEnded(value: GmEventEndedState): void {
  lastEndedState = value;
  lastEndedListeners.forEach((l) => l());
}

/** Dismiss the persistent results (results HUD ✕ / Event Control). */
export function clearLastEnded(): void {
  setLastEnded(null);
}

/** Subscribe to the persistent last-race-results state. */
export function useLastEnded(): GmEventEndedState {
  return useSyncExternalStore(
    (cb) => {
      lastEndedListeners.add(cb);
      return () => lastEndedListeners.delete(cb);
    },
    () => lastEndedState,
  );
}

// ── Schema snapshot — the live event per the room schema (M2: schema is
// the truth). 500ms poll, the RaceLobbyAutoOpener pattern. ──────────────

export interface GmEventSchemaSnapshot {
  id: string;
  phase: string;
  title: string;
  secondsRemaining: number;
  participantCount: number;
  templateId: string;
}

function readGmEventSchema(): GmEventSchemaSnapshot | null {
  const gm = getRoom()?.state?.gmEvent;
  if (!gm?.id) return null;
  return {
    id: gm.id,
    phase: gm.phase ?? '',
    title: gm.title ?? '',
    secondsRemaining: gm.secondsRemaining ?? 0,
    participantCount: gm.participantCount ?? 0,
    templateId: gm.templateId ?? '',
  };
}

const SCHEMA_POLL_MS = 500;

/** Live event snapshot off the room schema — null when no active event. */
export function useGmEventSchema(): GmEventSchemaSnapshot | null {
  const [snap, setSnap] = useState<GmEventSchemaSnapshot | null>(readGmEventSchema);
  useEffect(() => {
    const tick = () => {
      const next = readGmEventSchema();
      setSnap((prev) => {
        if (prev === next) return prev;
        if (prev && next && JSON.stringify(prev) === JSON.stringify(next)) return prev;
        return next;
      });
    };
    tick();
    const interval = setInterval(tick, SCHEMA_POLL_MS);
    return () => clearInterval(interval);
  }, []);
  return snap;
}

// ── Imperative send helpers (no hook needed) ──────────────────────

export const GmEventActions = {
  createFromTemplate(templateId: string): void {
    sendToServer('gmEventCreate', { templateId });
  },
  create(definition: any): void {
    sendToServer('gmEventCreate', { definition });
  },
  start(eventId: string): void {
    sendToServer('gmEventStart', { eventId });
  },
  join(eventId: string): void {
    sendToServer('gmEventJoin', { eventId });
  },
  leave(eventId: string): void {
    sendToServer('gmEventLeave', { eventId });
  },
  phaseAdvance(eventId: string, targetPhase?: string): void {
    sendToServer('gmEventPhaseAdvance', { eventId, targetPhase });
  },
  cancel(eventId: string, reason?: string): void {
    sendToServer('gmEventCancel', { eventId, reason });
  },
  warp(eventId: string, target: string = 'participants'): void {
    sendToServer('gmEventWarp', { eventId, target });
  },
  tag(eventId: string, targetSessionId: string): void {
    sendToServer('gmEventTag', { eventId, targetSessionId });
  },
  fishingCast(eventId: string): void {
    sendToServer('gmEventFishingCast', { eventId });
  },
  fishingReel(eventId: string): void {
    sendToServer('gmEventFishingReel', { eventId });
  },
  /** Request the server's template catalog (responds via direct send). */
  templateList(): void {
    sendToServer('gmEventTemplateList');
  },
  /** Pull the last race's results (reload/late-join recovery — the server's
   *  join-time push races the fresh page's handler binding). */
  resultsQuery(): void {
    sendToServer('gmEventResultsQuery');
  },
};

// One-shot per page load: once the room exists AND one bind cycle has
// passed (useColyseusMessages registers handlers within the same 500ms
// poll), pull the last results so a reloading client on the post-race
// lobby recovers the podium the direct push already missed.
let resultsQueried = false;
function queryResultsOnceReady(): void {
  if (resultsQueried) return;
  if (!getRoom()) return;
  resultsQueried = true;
  setTimeout(() => GmEventActions.resultsQuery(), 700);
}

// ── useGmEvent hook ───────────────────────────────────────────────

export function useGmEvent(): GmEventState & { templates: GmEventTemplateInfo[] | null; actions: typeof GmEventActions } {
  const [announcement, setAnnouncement] = useState<GmEventAnnouncement | null>(null);
  const [scoreboard, setScoreboard] = useState<GmEventScoreboard | null>(null);
  const [ended, setEnded] = useState<GmEventState['ended']>(null);
  const [cancelled, setCancelled] = useState<GmEventState['cancelled']>(null);
  const [fishingCatch, setFishingCatch] = useState<GmEventState['fishingCatch']>(null);
  const [fishingPlayerState, setFishingPlayerState] = useState<GmEventState['fishingPlayerState']>(null);
  const [racePlayerState, setRacePlayerState] = useState<GmEventState['racePlayerState']>(null);
  const [raceFinish, setRaceFinish] = useState<GmEventState['raceFinish']>(null);
  const [templates, setTemplates] = useState<GmEventTemplateInfo[] | null>(null);

  useColyseusMessages({
    gmEventAnnouncement: (msg: GmEventAnnouncementWire) => {
      if (!msg?.eventId) return;
      setAnnouncement({
        eventId: msg.eventId,
        title: msg.title,
        phase: msg.phase,
        secondsRemaining: msg.secondsRemaining,
        participantCount: msg.participantCount,
        maxParticipants: msg.maxParticipants,
        bornAt: Date.now(),
      });
    },
    gmEventCreated: (_msg: GmEventCreatedWire) => {
      // Event created — the announcement will follow from the engine.
    },
    gmEventPhaseChanged: (msg: GmEventPhaseChangedWire) => {
      if (!msg?.eventId) return;
      setAnnouncement(prev => prev ? {
        ...prev,
        eventId: msg.eventId,
        phase: msg.phase,
        bornAt: Date.now(),
      } : null);
      // A NEW event reaching staging supersedes the persistent results
      // (mirrors the server's lastEnded retention semantics).
      if (msg.phase === 'staging' && lastEndedState && msg.eventId !== lastEndedState.eventId) {
        setLastEnded(null);
      }
    },
    gmEventScoreboard: (msg: GmEventScoreboardWire) => {
      if (!msg?.eventId) return;
      setScoreboard(msg.scoreboard);
    },
    gmEventEnded: (msg: GmEventEndedWire) => {
      if (!msg?.eventId) return;
      setEnded({ eventId: msg.eventId, winnerIds: msg.winnerIds, scoreboard: msg.scoreboard });
      // Persistent copy — survives the 15s flash; covers the server's
      // join-time re-send (reload/late-join on the post-race lobby).
      setLastEnded({ eventId: msg.eventId, winnerIds: msg.winnerIds, scoreboard: msg.scoreboard });
      setAnnouncement(null);
    },
    gmEventCancelled: (msg: GmEventCancelledWire) => {
      if (!msg?.eventId) return;
      setCancelled({ eventId: msg.eventId, reason: msg.reason });
      setAnnouncement(null);
    },
    gmEventFishingCatch: (msg: GmEventFishingCatchWire) => {
      if (!msg?.eventId) return;
      setFishingCatch({
        sessionId: msg.sessionId,
        fishName: msg.fishName,
        rarity: msg.rarity,
        weight: msg.weight,
        points: msg.points,
      });
    },
    gmEventFishingPlayerState: (msg: GmEventFishingPlayerStateWire) => {
      if (!msg?.eventId) return;
      setFishingPlayerState({
        sessionId: msg.sessionId,
        status: msg.status,
        totalScore: msg.totalScore,
        totalWeight: msg.totalWeight,
      });
    },
    gmEventRacePlayerState: (msg: GmEventRacePlayerStateWire) => {
      if (!msg?.eventId) return;
      setRacePlayerState({
        sessionId: msg.sessionId,
        status: msg.status,
        checkpointIndex: msg.checkpointIndex,
        lap: msg.lap,
        place: msg.place,
        finishTimeMs: msg.finishTimeMs,
        totalCheckpoints: msg.totalCheckpoints,
      });
    },
    gmEventRaceFinish: (msg: GmEventRaceFinishWire) => {
      if (!msg?.eventId) return;
      setRaceFinish({
        sessionId: msg.sessionId,
        place: msg.place,
        finishTimeMs: msg.finishTimeMs,
      });
    },
    gmEventTemplateList: (msg: GmEventTemplateListWire) => {
      if (!Array.isArray(msg?.templates)) return;
      setTemplates(msg.templates);
    },
  });

  // Reload/late-join results recovery — one-shot per page (module flag).
  useEffect(() => {
    queryResultsOnceReady();
    const interval = setInterval(() => {
      queryResultsOnceReady();
      if (resultsQueried) clearInterval(interval);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Auto-clear announcements after timeout.
  const announcementRef = useRef<GmEventAnnouncement | null>(null);
  useEffect(() => {
    announcementRef.current = announcement;
    if (!announcement) return;
    const targetBornAt = announcement.bornAt;
    const timer = setTimeout(() => {
      if (announcementRef.current?.bornAt === targetBornAt) {
        setAnnouncement(null);
      }
    }, ANNOUNCEMENT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [announcement]);

  // Auto-clear ended/cancelled after 15s.
  useEffect(() => {
    if (!ended) return;
    const timer = setTimeout(() => setEnded(null), 15_000);
    return () => clearTimeout(timer);
  }, [ended]);

  useEffect(() => {
    if (!cancelled) return;
    const timer = setTimeout(() => setCancelled(null), 15_000);
    return () => clearTimeout(timer);
  }, [cancelled]);

  // Auto-clear fishing catch after 5s.
  useEffect(() => {
    if (!fishingCatch) return;
    const timer = setTimeout(() => setFishingCatch(null), 5_000);
    return () => clearTimeout(timer);
  }, [fishingCatch]);

  // Auto-clear race finish after 8s.
  useEffect(() => {
    if (!raceFinish) return;
    const timer = setTimeout(() => setRaceFinish(null), 8_000);
    return () => clearTimeout(timer);
  }, [raceFinish]);

  // Mirror state to module-level for non-React access.
  useEffect(() => {
    currentState = {
      announcement, scoreboard, ended, cancelled,
      fishingCatch, fishingPlayerState, racePlayerState, raceFinish,
    };
  }, [announcement, scoreboard, ended, cancelled, fishingCatch, fishingPlayerState, racePlayerState, raceFinish]);

  return {
    announcement,
    scoreboard,
    ended,
    cancelled,
    fishingCatch,
    fishingPlayerState,
    racePlayerState,
    raceFinish,
    templates,
    actions: GmEventActions,
  };
}
